import _ from 'lodash';
import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/compat/router';

import { mediaQueries } from 'shared/styles';
import DeliveryErrorModal from 'checkout/modals/delivery-validation-error';
import Header from 'checkout/components/header';
import Sections from 'checkout/components/sections';
import Confirmation from 'checkout/components/confirmation';
import onCheckoutMount from 'src/checkout/actions/on-checkout-mount';
import useStores from 'shared/hooks/use-stores';
import useApolloClient from 'shared/hooks/use-apollo-client';
import { useCheckout } from 'checkout/context';
import useErnie from 'shared/hooks/use-ernie';
import { useViewedCheckoutAnalytics } from 'hooks/use-ecommerce-analytics';
import { useIsolatedCheckout } from 'checkout/isolated/context';
import { setUIFromQueryParams } from 'checkout/isolated/set-ui-from-query-params';
import { setCustomerFromQueryParams } from 'shared/hooks/use-customer-from-query-params';
import { ORDER_TYPE_PICKUP, ORDER_TYPE_IN_STORE_PICKUP } from 'shared/constants';
import { isDutchiePlusKioskOrder } from 'shared/helpers/checkout';

import useStoreMap from 'checkout/isolated/use-store-map';
import useDataMap from 'checkout/isolated/use-data-map';

import SectionsSkeleton from 'checkout/isolated/sections-skeleton';
import ConfirmationSkeleton from 'checkout/isolated/confirmation-skeleton';

function IsolatedCheckoutContainer() {
  // Bypass the bfcache to reload checkout
  useEffect(() => {
    const handleShow = (event) => {
      if (event.persisted) {
        window.location.reload();
      }
    };

    window.addEventListener('pageshow', handleShow);

    return () => {
      window.removeEventListener('pageshow', handleShow);
    };
  }, []);

  useStoreMap();
  useDataMap();

  const { Cart, User, UI } = useStores();
  const apolloClient = useApolloClient();
  const showErnie = useErnie();
  const CheckoutCtx = useCheckout();
  const { loading } = useIsolatedCheckout();
  const router = useRouter();
  const queryParams = router?.query;
  const ALLOWED_PARAMS = [`r`, `promo`, `autoLogin`];
  const isDutchiePlusKiosk = isDutchiePlusKioskOrder(Cart?.orderType, UI?.isPlus);

  const enableOmniauthLogin = Cart.dispensary?.storeSettings?.enableOmniauthLoginCheckout ?? false;

  if (Cart?.orderType === ORDER_TYPE_IN_STORE_PICKUP) {
    Cart.setOrderType(ORDER_TYPE_PICKUP);
  }

  useViewedCheckoutAnalytics({ cart: Cart, isLoading: loading });

  // we dont want to run checkout mount until we at least know what dispensary the cart is for.
  useEffect(() => {
    if (Cart?.order?.dispensary && loading === false) {
      // If external user details have already been derived from the query params,
      // we don't need to do that again. We just need to sync them over to guestCustomer.
      if (User?.hasExternalUser && !Cart.order.guestCustomer.email) {
        const { userDetailsFromExternalSource } = User;
        _.mapKeys(userDetailsFromExternalSource, (value, key) => Cart.updateGuestCustomer(key, value));
      } else {
        const isUserLoggedIn = User?.isLoggedIn;
        setCustomerFromQueryParams({ Cart, queryParams, isUserLoggedIn });
      }

      setUIFromQueryParams({ UI, queryParams });

      if (enableOmniauthLogin) {
        if (queryParams?.token && !User?.token) {
          User?.updateToken(queryParams.token);
        }

        const params = { ...queryParams };

        Object.keys(params).forEach((key) => {
          if (!ALLOWED_PARAMS.includes(key)) {
            delete params[key];
          }
        });

        const finalParams = new URLSearchParams(params).toString();
        const [pathName] = router?.asPath?.split(`?`) ?? [''];
        const urlToReplace = finalParams ? `${pathName}?${finalParams}` : `${pathName}`;

        if (router?.asPath !== urlToReplace) {
          router?.replace(urlToReplace, undefined, { shallow: true });
        }
      }

      onCheckoutMount({ UI, Cart, User, apolloClient, showErnie, CheckoutCtx });

      if (isDutchiePlusKiosk && Cart) {
        Cart.isKioskOrder = true;
        Cart.setReservationType(`asap`);
        UI?.setHideLogin(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    Cart?.order?.dispensary?.id,
    loading,
    Cart?.isKioskOrder,
    Cart?.setReservationType,
    isDutchiePlusKiosk,
    UI?.setHideLogin,
    queryParams,
    User?.token,
    User?.updateToken,
    enableOmniauthLogin,
  ]);

  if (!Cart?.order?.cart) {
    return null;
  }

  return (
    <>
      <Container>
        <CheckoutForm>
          <Header />
          {loading && <SectionsSkeleton />}
          {!loading && <Sections />}
        </CheckoutForm>

        <Overview>
          {loading && <ConfirmationSkeleton />}
          {!loading && <Confirmation />}
        </Overview>
      </Container>
      {CheckoutCtx.viewingDeliveryErrorModal && <DeliveryErrorModal />}
    </>
  );
}

export default observer(IsolatedCheckoutContainer);

const Container = styled.div`
  display: flex;
  justify-content: center;
  max-width: 990px;
  margin: 0 auto;

  @media ${mediaQueries.desktop} {
    display: block;
    max-width: none;
    margin: 0;
  }
`;

const CheckoutForm = styled.div`
  flex: 1 0 0%;
  max-width: 640px;
  min-width: 0;
  padding-left: 0;
  padding-right: 40px;
  padding-bottom: 300px;

  @media ${mediaQueries.desktop} {
    margin: 0 auto;
    padding-right: 0;
    padding-bottom: 30px;
  }

  @media ${mediaQueries.tablet} {
    padding-bottom: 20px;
  }

  @media ${mediaQueries.largePhone} {
    padding-right: 0;
    padding-left: 0;
    max-width: 450px;
    width: 100%;
  }
`;

const Overview = styled.div`
  flex: 0 0 307px;
  padding-right: 0;

  @media ${mediaQueries.largePhone} {
    width: 100%;
  }
`;
